// Releases — follow a version through environments (dev → stage → prod) and
// see where it stalled, failed, or succeeded. Port of the legacy ReleasesLens
// grouping, plus a per-environment lens as a secondary tab.

import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Search as SearchIcon, LocalShipping as ReleasesIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { useV2Filters } from '../hooks/useV2Filters';
import { groupReleases, sortEnvNames } from '../lib/grouping';
import { deploymentKey } from '../lib/records';
import { getColors, rounded } from '../theme/tokens';
import { getStatusHue } from '../theme/statusTokens';
import {
  ColumnConstraints,
  ColumnWidths,
  useColumnWidths,
} from '../hooks/useColumnWidths';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import DeploymentRow from '../components/DeploymentRow';
import ColumnResizeHandle from '../components/ColumnResizeHandle';
import InfoPopover from '../components/InfoPopover';
import { timeAgo, formatTimestampShort } from '../lib/format';

const MAX_RELEASES = 200;

// Cap each "By environment" column's height so the page doesn't grow unbounded;
// the record list scrolls internally while the env header stays put.
const ENV_COLUMN_MAX_HEIGHT = 520;

const COLUMN_WIDTHS_KEY = 'cd-dashboard-v2-releases-column-widths';

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  service: 280,
  version: 130,
  time: 110,
};

const COLUMN_CONSTRAINTS: ColumnConstraints = {
  service: { min: 120, max: 520 },
  version: { min: 60, max: 260 },
  time: { min: 70, max: 200 },
};

// Mono-caps eyebrow labels, matching the Activity feed header.
const headerLabelSx = (c: ReturnType<typeof getColors>) => ({
  color: c.inkMuted,
  fontWeight: 600,
  fontSize: '0.68rem',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
});

const ReleasesPage: React.FC = () => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { staticRecords } = useDeploymentData();
  const { envs: selectedEnvs, setEnvs } = useV2Filters();
  const [lens, setLens] = useState<'version' | 'env'>('version');
  const [query, setQuery] = useState('');
  const { widths, setWidth, resetWidth } = useColumnWidths(
    COLUMN_WIDTHS_KEY,
    DEFAULT_COLUMN_WIDTHS,
    COLUMN_CONSTRAINTS
  );

  const releases = useMemo(() => groupReleases(staticRecords), [staticRecords]);

  const visibleReleases = useMemo(() => {
    const q = query.toLowerCase().trim();
    const filtered = q
      ? releases.filter(
          r =>
            r.app.toLowerCase().includes(q) ||
            r.service.toLowerCase().includes(q) ||
            r.version.toLowerCase().includes(q)
        )
      : releases;
    return filtered.slice(0, MAX_RELEASES);
  }, [releases, query]);

  const envColumns = useMemo(() => {
    const q = query.toLowerCase().trim();
    const matches = q
      ? staticRecords.filter(
          r =>
            (r.app || '').toLowerCase().includes(q) ||
            (r.serviceName || '').toLowerCase().includes(q) ||
            (r.version || '').toLowerCase().includes(q)
        )
      : staticRecords;
    const byEnv = new Map<string, typeof staticRecords>();
    matches.forEach(r => {
      const env = r.envName || r.env || 'unknown';
      const list = byEnv.get(env);
      if (list) list.push(r);
      else byEnv.set(env, [r]);
    });
    const envs = sortEnvNames(Array.from(byEnv.keys()));
    return envs.map(env => ({
      env,
      records: (byEnv.get(env) || [])
        .slice()
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 12),
    }));
  }, [staticRecords, query]);

  // Full env list for the toggle chips — must list every env, not just the
  // selected ones. Empty selection = show all (default, unchanged behavior).
  const allEnvs = useMemo(() => envColumns.map(col => col.env), [envColumns]);

  const toggleEnv = (env: string) =>
    setEnvs(
      selectedEnvs.includes(env)
        ? selectedEnvs.filter(e => e !== env)
        : [...selectedEnvs, env]
    );

  const shownColumns =
    selectedEnvs.length === 0
      ? envColumns
      : envColumns.filter(col => selectedEnvs.includes(col.env));

  return (
    <Box>
      <PageHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Releases
            <InfoPopover entry="releasesLens" size={16} />
          </Box>
        }
        subtitle="Track versions as they move through environments"
        actions={
          <Tabs value={lens} onChange={(_, v) => setLens(v)} sx={{ minHeight: 36 }}>
            <Tab label="By version" value="version" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="By environment" value="env" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>
        }
      />

      {lens === 'version' ? (
        <>
          <TextField
            size="small"
            placeholder="Filter by app, service, or version…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: c.inkFaint }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320, mb: 2 }}
          />

          {visibleReleases.length === 0 ? (
            <EmptyState
              icon={<ReleasesIcon />}
              title="No releases to show"
              body="Releases appear once versioned deployments report in."
            />
          ) : (
            <Paper
              variant="outlined"
              sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
            >
              {/* Column header with drag handles (double-click resets). Mirrors
                  the row layout below so labels track their columns. */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 0.75,
                  borderBottom: `1px solid ${c.hairline}`,
                  backgroundColor: c.canvasSoft,
                  minWidth: 0,
                }}
              >
                <Box sx={{ position: 'relative', width: widths.service, flexShrink: 0, minWidth: 0 }}>
                  <Typography sx={headerLabelSx(c)} noWrap>
                    Service
                  </Typography>
                  <ColumnResizeHandle
                    width={widths.service}
                    onResize={px => setWidth('service', px)}
                    onReset={() => resetWidth('service')}
                  />
                </Box>
                <Box sx={{ position: 'relative', width: widths.version, flexShrink: 0 }}>
                  <Typography sx={headerLabelSx(c)} noWrap>
                    Version
                  </Typography>
                  <ColumnResizeHandle
                    width={widths.version}
                    onResize={px => setWidth('version', px)}
                    onReset={() => resetWidth('version')}
                  />
                </Box>
                <Typography sx={headerLabelSx(c)} noWrap>
                  Environments
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ position: 'relative', width: widths.time, textAlign: 'right', flexShrink: 0 }}>
                  <Typography sx={headerLabelSx(c)} noWrap>
                    Updated
                  </Typography>
                  <ColumnResizeHandle
                    width={widths.time}
                    onResize={px => setWidth('time', px)}
                    onReset={() => resetWidth('time')}
                    side="left"
                  />
                </Box>
              </Box>
              {visibleReleases.map(rel => {
                const envs = sortEnvNames(Array.from(rel.envMap.keys()));
                return (
                  <Box
                    key={rel.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      borderBottom: `1px solid ${c.hairline}`,
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: c.hoverWash },
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ width: widths.service, minWidth: 0, flexShrink: 0, overflow: 'hidden' }}>
                      <MuiLink
                        component={RouterLink}
                        to={`/services/${encodeURIComponent(rel.app)}/${encodeURIComponent(rel.service)}`}
                        underline="hover"
                        sx={{ color: c.ink, fontWeight: 600, fontSize: '0.9rem', display: 'block' }}
                        noWrap
                      >
                        {rel.app}
                        <Box component="span" sx={{ color: c.inkMuted, fontWeight: 400 }}>
                          {' / '}
                          {rel.service}
                        </Box>
                      </MuiLink>
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: c.inkSecondary,
                        width: widths.version,
                        flexShrink: 0,
                      }}
                      noWrap
                      title={rel.version}
                    >
                      {rel.version}
                    </Typography>

                    {/* Env progression */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flexWrap: 'wrap' }}>
                      {envs.map((env, i) => {
                        const rec = rel.envMap.get(env)!;
                        const hue = getStatusHue(rec.status);
                        return (
                          <React.Fragment key={env}>
                            {i > 0 && (
                              <Typography
                                sx={{ color: c.inkFaint, fontSize: '0.7rem', mt: '2px', alignSelf: 'flex-start' }}
                              >
                                →
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.25,
                              }}
                            >
                              <Tooltip
                                title={`${env}: ${rec.status || 'unknown'} · ${timeAgo(rec.timestamp)}`}
                                arrow
                              >
                                <Chip
                                  label={env}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    backgroundColor: `${hue}14`,
                                    color: hue,
                                    border: `1px solid ${hue}44`,
                                  }}
                                />
                              </Tooltip>
                              <Typography
                                sx={{
                                  fontSize: '0.62rem',
                                  color: c.inkFaint,
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1,
                                }}
                              >
                                {formatTimestampShort(rec.timestamp)}
                              </Typography>
                            </Box>
                          </React.Fragment>
                        );
                      })}
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: c.inkMuted, width: widths.time, textAlign: 'right', flexShrink: 0 }}
                      noWrap
                    >
                      {timeAgo(rel.maxTimestamp)}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          )}
          {releases.length > MAX_RELEASES && (
            <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', mt: 1 }}>
              Showing the {MAX_RELEASES} most recent releases of {releases.length}. Narrow with the
              filter above.
            </Typography>
          )}
        </>
      ) : (
        /* By environment: recent deployments per env, side by side */
        <>
          <TextField
            size="small"
            placeholder="Filter by app, service, or version…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: c.inkFaint }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320, mb: 2, display: 'block' }}
          />

          {/* Env toggle chips — click to show/hide a column. No selection
              means every environment is shown. */}
          {allEnvs.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {allEnvs.map(env => {
                const on = selectedEnvs.length === 0 || selectedEnvs.includes(env);
                const hue = getStatusHue('success');
                return (
                  <Chip
                    key={env}
                    label={env}
                    size="small"
                    onClick={() => toggleEnv(env)}
                    sx={{
                      height: 24,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      backgroundColor: on ? `${hue}14` : 'transparent',
                      color: on ? hue : c.inkFaint,
                      border: `1px solid ${on ? `${hue}44` : c.hairline}`,
                      '&:hover': { backgroundColor: on ? `${hue}22` : c.hoverWash },
                    }}
                  />
                );
              })}
              {selectedEnvs.length > 0 && (
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => setEnvs([])}
                  underline="hover"
                  sx={{ fontSize: '0.75rem', color: c.inkMuted, ml: 0.5 }}
                >
                  Show all
                </MuiLink>
              )}
            </Box>
          )}

          {shownColumns.length === 0 ? (
            <EmptyState
              icon={<ReleasesIcon />}
              title="No environments selected"
              body="Pick at least one environment above to see its recent deployments."
            />
          ) : (
            <Box
              sx={{
                display: 'grid',
                // Cap columns at the responsive max, but never make more tracks
                // than there are envs to show — so 1 or 2 selected fill the width.
                gridTemplateColumns: {
                  xs: '1fr',
                  md: `repeat(${Math.min(shownColumns.length, 2)}, 1fr)`,
                  lg: `repeat(${Math.min(shownColumns.length, 3)}, 1fr)`,
                },
                gap: 2,
              }}
            >
              {shownColumns.map(({ env, records: envRecords }) => (
                <Paper
                  key={env}
                  variant="outlined"
                  sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderBottom: `1px solid ${c.hairline}`,
                      backgroundColor: c.canvasSoft,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                        color: c.inkMuted,
                      }}
                    >
                      {env}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <MuiLink
                      component={RouterLink}
                      to={`/environments/${encodeURIComponent(env)}`}
                      underline="hover"
                      sx={{ fontSize: '0.75rem', color: c.primary }}
                    >
                      Open
                    </MuiLink>
                  </Box>
                  <Box sx={{ maxHeight: ENV_COLUMN_MAX_HEIGHT, overflowY: 'auto' }}>
                    {envRecords.map((r, i) => (
                      <DeploymentRow key={deploymentKey(r, i)} record={r} hideEnv />
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ReleasesPage;
